/**
 * Query-string helpers for the paginated list endpoints.
 *
 * These URLs used to be built by raw template interpolation, so any value
 * containing `&`, `#`, `+` or a space silently corrupted the request — and
 * filter values are data-derived (contact, candidate and recipient names), not
 * just hand-typed search terms.
 *
 * Values are percent-encoded; keys are too, except for the square brackets
 * that carry Laravel's `filters[key]` array syntax. `URLSearchParams` would
 * also work (PHP decodes `%5B`/`%5D` fine) but it would rewrite the shape of
 * every list request for no benefit — this keeps the wire format identical to
 * what the API already receives, apart from genuinely unsafe characters.
 */

const encodeValue = (value) => encodeURIComponent(String(value));

const encodeKey = (key) =>
  encodeURIComponent(String(key)).replace(/%5B/gi, "[").replace(/%5D/gi, "]");

const isPresent = ([, value]) => value !== undefined && value !== null;

/**
 * Turns `{ type: ["a", "b"], read: "unread" }` into
 * `[["filters[type]", "a,b"], ["filters[read]", "unread"]]`.
 */
function filterEntries(filters) {
  return Object.entries(filters ?? {})
    .filter(isPresent)
    .map(([key, value]) => [
      `filters[${key}]`,
      Array.isArray(value) ? value.join(",") : value,
    ]);
}

/**
 * Builds an encoded query string from ordinary params plus a `filters` object.
 * Empty strings are kept (the API expects `search=` when nothing is searched);
 * only `undefined`/`null` are dropped.
 */
export function buildQueryString(params = {}, filters = {}) {
  return [...Object.entries(params).filter(isPresent), ...filterEntries(filters)]
    .map(([key, value]) => `${encodeKey(key)}=${encodeValue(value)}`)
    .join("&");
}

/** `buildQueryString` plus the path, omitting the `?` when there's nothing to send. */
export default function buildListUrl(path, params = {}, filters = {}) {
  const queryString = buildQueryString(params, filters);
  return queryString ? `${path}?${queryString}` : path;
}
